import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../config/api';
import toast from 'react-hot-toast';
import { FaWallet, FaRupeeSign, FaHistory, FaCheckCircle, FaClock, FaTimesCircle, FaSpinner } from 'react-icons/fa';

const Earnings = () => {
  const { user, updateUser } = useAuth();
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [withdrawalNotes, setWithdrawalNotes] = useState('');
  const [withdrawalLoading, setWithdrawalLoading] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);

  useEffect(() => {
    fetchWithdrawals();
  }, []);

  // Auto-refresh user data when earnings page is active
  useEffect(() => {
    if (!user || user.role !== 'rider') return;

    let refreshInterval = null;
    
    const refreshUserData = async () => {
      if (document.visibilityState === 'visible') {
        try {
          const response = await api.get('/api/users/profile');
          updateUser(response.data.user);
        } catch (error) {
          if (error.response?.status === 429) {
            console.warn('Rate limited on earnings refresh. Slowing down...');
            if (refreshInterval) {
              clearInterval(refreshInterval);
              refreshInterval = null;
            }
            setTimeout(() => {
              refreshInterval = setInterval(refreshUserData, 60000);
            }, 60000);
          } else {
            console.error('Error refreshing user data:', error);
          }
        }
      }
    };

    refreshUserData();
    refreshInterval = setInterval(refreshUserData, 60000);

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const fetchWithdrawals = async () => {
    try {
      const response = await api.get('/api/payouts/my-requests');
      setWithdrawals(response.data.payouts);
    } catch (error) {
      console.error('Fetch withdrawals error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawalRequest = async () => {
    if (!withdrawalAmount || parseFloat(withdrawalAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const amount = parseFloat(withdrawalAmount);
    const walletBalance = user?.wallet?.balance || 0;
    const pendingPlatformFees = user?.wallet?.pendingPlatformFees || 0;
    const availableBalance = Math.max(0, walletBalance - pendingPlatformFees);

    if (amount > availableBalance) {
      toast.error(`Insufficient available balance. Available: ₹${availableBalance.toFixed(2)}`);
      return;
    }

    if (amount < 100) {
      toast.error('Minimum withdrawal amount is ₹100');
      return;
    }

    setWithdrawalLoading(true);
    try {
      const response = await api.post('/api/payouts/request-withdrawal', {
        amount,
        notes: withdrawalNotes
      });

      toast.success(`Withdrawal request of ₹${amount} submitted successfully`);
      setShowWithdrawalModal(false);
      setWithdrawalAmount('');
      setWithdrawalNotes('');
      
      // Refresh user data and withdrawals
      const userResponse = await api.get('/api/users/profile');
      updateUser(userResponse.data.user);
      fetchWithdrawals();
    } catch (error) {
      console.error('Withdrawal request error:', error);
      const message = error.response?.data?.message || 'Failed to submit withdrawal request';
      toast.error(message);
    } finally {
      setWithdrawalLoading(false);
    }
  };

  const handleCancelWithdrawal = async (payoutId) => {
    if (!window.confirm('Are you sure you want to cancel this withdrawal request?')) {
      return;
    }

    try {
      await api.put(`/api/payouts/${payoutId}/cancel`);
      toast.success('Withdrawal request cancelled');
      fetchWithdrawals();
      
      // Refresh user data
      const userResponse = await api.get('/api/users/profile');
      updateUser(userResponse.data.user);
    } catch (error) {
      console.error('Cancel withdrawal error:', error);
      toast.error('Failed to cancel withdrawal request');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      requested: 'badge-warning',
      pending: 'badge-warning',
      processing: 'badge-info',
      completed: 'badge-success',
      failed: 'badge-danger',
      cancelled: 'badge-secondary',
      rejected: 'badge-danger'
    };

    const icons = {
      requested: <FaClock className="inline mr-1" />,
      pending: <FaClock className="inline mr-1" />,
      processing: <FaSpinner className="inline mr-1 animate-spin" />,
      completed: <FaCheckCircle className="inline mr-1" />,
      failed: <FaTimesCircle className="inline mr-1" />,
      cancelled: <FaTimesCircle className="inline mr-1" />,
      rejected: <FaTimesCircle className="inline mr-1" />
    };

    return (
      <span className={`badge ${badges[status] || 'badge-secondary'}`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>
    );
  };

  const pendingWithdrawal = withdrawals.find(w => ['requested', 'pending', 'processing'].includes(w.status));
  const walletBalance = user?.wallet?.balance || 0;
  const pendingPlatformFees = user?.wallet?.pendingPlatformFees || 0;
  const availableBalance = Math.max(0, walletBalance - pendingPlatformFees);

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      {/* Mobile Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 md:hidden">
        <div className="px-4 py-4">
          <h1 className="text-xl font-bold text-gray-900">Earnings</h1>
        </div>
      </div>

      <div className="container mx-auto px-4 py-4 md:py-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="hidden md:block text-3xl font-bold text-gray-800 mb-8">Earnings</h1>

          {/* Wallet Balance Card */}
          <div className="bg-green-50 rounded-lg border border-green-200 mb-4 sm:mb-6">
            <div className="text-center py-6 px-4">
              <FaWallet className="text-4xl text-green-600 mx-auto mb-3" />
              <div className="text-4xl font-bold text-green-600 mb-2">
                ₹{availableBalance.toFixed(2)}
              </div>
              <p className="text-sm text-gray-600 mb-4">Available Balance</p>
              <button
                onClick={() => setShowWithdrawalModal(true)}
                disabled={!user?.bankDetails?.accountNumber || availableBalance < 100}
                className={`bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors flex items-center justify-center mx-auto ${
                  !user?.bankDetails?.accountNumber || availableBalance < 100
                    ? 'opacity-50 cursor-not-allowed'
                    : ''
                }`}
              >
                <FaRupeeSign className="inline mr-2" />
                Request Withdrawal
              </button>
              {!user?.bankDetails?.accountNumber && (
                <p className="text-xs text-gray-500 mt-3">
                  Please add bank details first
                </p>
              )}
              {availableBalance < 100 && user?.bankDetails?.accountNumber && (
                <p className="text-xs text-gray-500 mt-3">
                  Minimum withdrawal amount is ₹100
                </p>
              )}
            </div>
          </div>

          {/* Pending Withdrawal Alert */}
          {pendingWithdrawal && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-yellow-800 mb-1">Pending Withdrawal Request</p>
                  <p className="text-sm text-gray-700">
                    Amount: <span className="font-semibold">₹{pendingWithdrawal.amount.toFixed(2)}</span>
                    {' • '}
                    Status: {getStatusBadge(pendingWithdrawal.status)}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    Requested on: {new Date(pendingWithdrawal.createdAt).toLocaleString()}
                  </p>
                </div>
                {['requested', 'pending'].includes(pendingWithdrawal.status) && (
                  <button
                    onClick={() => handleCancelWithdrawal(pendingWithdrawal._id)}
                    className="btn-secondary text-sm"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Transaction History - Combined Wallet and Payout Transactions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 p-4 border-b border-gray-200 flex items-center">
              <FaHistory className="mr-2" />
              Transaction History
            </h3>
            {loading ? (
              <div className="text-center py-8">
                <FaSpinner className="inline animate-spin text-2xl text-gray-400" />
              </div>
            ) : (
              <div className="p-4 space-y-3">
                {(() => {
                  // Combine wallet transactions and payout transactions
                  const allTransactions = [];
                  
                  // Add wallet transactions
                  if (user?.wallet?.transactions?.length > 0) {
                    user.wallet.transactions.forEach((transaction, index) => {
                      // Determine transaction type from description
                      let transactionType = 'Payment for booking';
                      if (transaction.description?.includes('Payout refund')) {
                        transactionType = 'Payout refund';
                      } else if (transaction.description?.includes('Payout')) {
                        transactionType = 'Payout';
                      } else if (transaction.description?.includes('Bulk payout')) {
                        transactionType = 'Bulk payout';
                      } else if (transaction.description?.includes('Payment for booking')) {
                        transactionType = 'Payment for booking';
                      } else if (transaction.type === 'debit') {
                        transactionType = 'Debit';
                      }
                      
                      // Extract transaction ID from description if available
                      let transactionId = `WALLET_${new Date(transaction.date).getTime()}_${index}`;
                      const payoutMatch = transaction.description?.match(/PAYOUT_[a-z0-9_]+/i);
                      if (payoutMatch) {
                        transactionId = payoutMatch[0];
                      } else {
                        // Try to extract booking ID or other ID from description
                        const idMatch = transaction.description?.match(/[a-f0-9]{24}/i);
                        if (idMatch) {
                          transactionId = idMatch[0];
                        }
                      }
                      
                      allTransactions.push({
                        id: transactionId,
                        type: transactionType,
                        description: transaction.description,
                        amount: transaction.amount,
                        isCredit: transaction.type === 'credit',
                        date: transaction.date,
                        source: 'wallet'
                      });
                    });
                  }
                  
                  // Add payout transactions
                  withdrawals.forEach((withdrawal) => {
                    // Add payout transaction
                    allTransactions.push({
                      id: withdrawal.transactionId || withdrawal._id,
                      type: withdrawal.bulkPayout ? 'Bulk payout' : 'Payout',
                      description: `Payout to ${withdrawal.bankDetails?.accountNumber?.slice(-4) || 'bank account'}`,
                      amount: withdrawal.amount,
                      isCredit: false,
                      date: withdrawal.createdAt,
                      source: 'payout',
                      status: withdrawal.status,
                      payoutId: withdrawal._id
                    });
                    
                    // Add payout refund if status is cancelled/rejected
                    if (['cancelled', 'rejected', 'failed'].includes(withdrawal.status)) {
                      allTransactions.push({
                        id: `${withdrawal.transactionId || withdrawal._id}_REFUND`,
                        type: 'Payout refund',
                        description: `Refund for ${withdrawal.bulkPayout ? 'bulk payout' : 'payout'}`,
                        amount: withdrawal.amount,
                        isCredit: true,
                        date: withdrawal.updatedAt || withdrawal.createdAt,
                        source: 'payout_refund',
                        payoutId: withdrawal._id
                      });
                    }
                  });
                  
                  // Sort by date (newest first)
                  allTransactions.sort((a, b) => new Date(b.date) - new Date(a.date));
                  
                  if (allTransactions.length === 0) {
                    return (
                      <p className="text-gray-500 text-center py-8">No transactions yet</p>
                    );
                  }
                  
                  return allTransactions.map((transaction) => (
                    <div
                      key={transaction.id}
                      className="bg-gray-100 rounded-lg p-4 flex items-start justify-between"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-gray-800 text-sm sm:text-base mb-1">
                          {transaction.type}
                        </p>
                        <p className="text-xs text-gray-600 font-mono mb-1 break-all">
                          {transaction.id}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(transaction.date).toLocaleString('en-IN', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          })}
                        </p>
                      </div>
                      <div className="ml-4 text-right">
                        <p
                          className={`text-base sm:text-lg font-semibold ${
                            transaction.isCredit ? 'text-green-600' : 'text-red-600'
                          }`}
                        >
                          {transaction.isCredit ? '+' : '-'}₹{transaction.amount.toFixed(2)}
                        </p>
                        {transaction.source === 'payout' && 
                         ['requested', 'pending'].includes(transaction.status) && (
                          <button
                            onClick={() => handleCancelWithdrawal(transaction.payoutId)}
                            className="text-xs text-red-600 hover:text-red-800 mt-1"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>

          {/* Withdrawal Modal */}
          {showWithdrawalModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-md w-full p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Request Withdrawal</h3>
                
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">Available Balance:</p>
                  <p className="text-2xl font-bold text-green-600">₹{availableBalance.toFixed(2)}</p>
                  {pendingPlatformFees > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Total: ₹{walletBalance.toFixed(2)} | Pending Fees: ₹{pendingPlatformFees.toFixed(2)}
                    </p>
                  )}
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Withdrawal Amount (Min: ₹100)
                  </label>
                  <input
                    type="number"
                    value={withdrawalAmount}
                    onChange={(e) => setWithdrawalAmount(e.target.value)}
                    max={availableBalance}
                    min={100}
                    step="0.01"
                    className="input-field"
                    placeholder="Enter amount"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={withdrawalNotes}
                    onChange={(e) => setWithdrawalNotes(e.target.value)}
                    className="input-field"
                    rows="3"
                    placeholder="Add any notes..."
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowWithdrawalModal(false);
                      setWithdrawalAmount('');
                      setWithdrawalNotes('');
                    }}
                    className="btn-secondary flex-1"
                    disabled={withdrawalLoading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleWithdrawalRequest}
                    disabled={withdrawalLoading || !withdrawalAmount || parseFloat(withdrawalAmount) < 100}
                    className="btn-primary flex-1"
                  >
                    {withdrawalLoading ? (
                      <>
                        <FaSpinner className="inline mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Submit Request'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Earnings;

